import React from 'react';
import { Link } from 'react-router-dom';
import { trackCtaClick } from '../analytics/ctaGoal';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import { Users, MapPin, Zap } from 'lucide-react';
import '../App.css';

const FeaturesPage = () => (
  <>
  <RouteSeo path="/features" />
  <Helmet>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Chaptery",
      "url": "https://trychaptery.com",
      "applicationCategory": "WritingApplication",
      "operatingSystem": "macOS, Windows",
      "description": "A desktop writing app for novelists, with a manuscript editor, world building, a character relationship map, a storyboard, revision history and cloud sync in one application.",
      "offers": [
        { "@type": "Offer", "name": "Free Trial", "price": "0", "priceCurrency": "USD", "description": "Full access for one book, 30 days. No credit card required." },
        { "@type": "Offer", "name": "Cloud Monthly", "price": "9.99", "priceCurrency": "USD", "billingIncrement": "P1M", "description": "Full access, unlimited books, cancel anytime." },
        { "@type": "Offer", "name": "Cloud Annual", "price": "99.99", "priceCurrency": "USD", "billingIncrement": "P1Y", "description": "Full access, unlimited books, billed annually. Saves two months versus monthly." }
      ],
      "publisher": { "@type": "Organization", "name": "Chaptery", "url": "https://trychaptery.com" }
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Chaptery", "item": "https://trychaptery.com" },
        { "@type": "ListItem", "position": 2, "name": "Features", "item": "https://trychaptery.com/features" }
      ]
    })}</script>
  </Helmet>
  <div className="features-page">

    {/* Hero */}
    <section className="features-hero">
      <div className="container">
        <h1>Every tool your story needs, in one place</h1>
        <p className="features-hero-sub">
          Most writers stitch together four or five separate apps just to finish a book.
          Chaptery replaces them all: a distraction-free desktop editor, a plot-planning storyboard,
          a full world-building toolkit, a visual relationship mapper, a daily writing tracker,
          and cloud sync that works silently in the background.
        </p>
        <div className="hero-actions">
          <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('features')}>Create Your Account</Link>
          <Link to="/pricing" className="btn-secondary-large">See Pricing</Link>
        </div>
      </div>
    </section>

    {/* Feature: Write */}
    <section id="write" className="features-split" aria-labelledby="features-write-heading">
      <div className="container features-split-inner">
        <div className="features-split-text">
          <div className="features-tag">Write</div>
          <h2 id="features-write-heading">A focused editor built for long-form work</h2>
          <p>
            Chaptery's editor keeps your manuscript center stage. Start from a template and the
            right pages are set up for you, then work from an outline grouped into front matter,
            chapters and back matter. Collapse it when you want to disappear into your draft.
          </p>
          <ul className="features-list">
            <li>Poem pages that keep your line and stanza breaks in every export</li>
            <li>Part, Preface, Bibliography and Index pages, alongside your chapters and scenes</li>
            <li>Scene dividers drawn in your book's style: * * *, one of 32 built-in ornaments, or your own image</li>
            <li>Distraction-free full-screen mode</li>
            <li>Inline notes: a marker in your text, the detail in the panel</li>
            <li>Print straight from Chaptery: a standard submission manuscript, or pages that match your PDF</li>
          </ul>
        </div>
        <div className="features-split-visual">
          <img
            src="/img/features-templates.png"
            width="631"
            height="519"
            srcSet="/img/features-templates.png 631w, /img/features-templates@2x.png 1262w"
            sizes="(max-width: 560px) calc(100vw - 40px), (max-width: 768px) 520px, (max-width: 1140px) calc(50vw - 50px), 520px"
            alt="Chaptery's welcome screen: continue your book, or start from a Blank Book, Novel, Non-Fiction, Memoir or Poetry Collection template"
            className="features-screenshot"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </section>

    {/* Feature: World Building */}
    <section className="features-split features-split--alt">
      <div className="container features-split-inner">
        <div className="features-split-visual features-split-visual--world">
          <div className="features-mockup features-mockup--grid">
            <div className="mockup-entity-card">
              <div className="mockup-entity-icon teal"><Users size={28} strokeWidth={2} /></div>
              <div className="mockup-entity-label">Character</div>
            </div>
            <div className="mockup-entity-card">
              <div className="mockup-entity-icon green"><MapPin size={28} strokeWidth={2} /></div>
              <div className="mockup-entity-label">Location</div>
            </div>
            <div className="mockup-entity-card">
              <div className="mockup-entity-icon orange"><Zap size={28} strokeWidth={2} /></div>
              <div className="mockup-entity-label">Event</div>
            </div>
          </div>
        </div>
        <div className="features-split-text">
          <div className="features-tag">Build Your World</div>
          <h2>Keep every detail organised and connected</h2>
          <p>
            Chaptery tracks your characters, locations, and events in a structured
            world guide that lives alongside your manuscript. No more searching through old
            chapters to remember a character's eye color.
          </p>
          <ul className="features-list">
            <li>Characters with backstory, traits, and story roles</li>
            <li>Locations with descriptions and linked events</li>
            <li>Cross-series entities: one character across multiple books</li>
            <li>Search and filter your entire world instantly</li>
          </ul>
          <p>
            <Link to="/fantasy-writing-software">Writing fantasy? See how Chaptery holds a whole world and series <span aria-hidden="true">→</span></Link>
          </p>
        </div>
      </div>
    </section>

    {/* Feature: Relationship Pegboard */}
    <section className="features-split features-split--highlight">
      <div className="container features-split-inner">
        <div className="features-split-text">
          <div className="features-tag features-tag--orange">Map Connections</div>
          <h2>See your story's web at a glance</h2>
          <p>
            Chaptery's visual relationship map lets you place characters, locations, and events
            on a canvas and draw the connections between them: alliances, rivalries, mentorships,
            and more. It's the kind of bird's-eye view that makes complex plots manageable.
          </p>
          <ul className="features-list">
            <li>Drag-and-drop canvas connecting characters, locations, and events</li>
            <li>Built-in relationship types (ally, rival, mentor, family, romantic), plus your own</li>
            <li>Name and label every connection between entities</li>
            <li>Zoom out to see your entire cast in one view</li>
          </ul>
          <p className="features-callout">
            This is the feature writers ask for most, and the one almost no tool provides.
          </p>
        </div>
        <div className="features-split-visual">
          <img src="/img/worldbuildingimage.png" alt="Chaptery relationship map" className="features-screenshot" />
        </div>
      </div>
    </section>

    {/* Feature: Storyboard */}
    <section className="features-split features-split--alt">
      <div className="container features-split-inner">
        <div className="features-split-visual">
          <img src="/img/Storyboard-image.png" alt="Chaptery storyboard canvas" className="features-screenshot" />
        </div>
        <div className="features-split-text">
          <div className="features-tag">Plan Your Plot</div>
          <h2>A corkboard for the story in your head</h2>
          <p>
            The Storyboard is a free-form sticky-note canvas where you can lay out every beat,
            scene, and turning point in your book. Drag notes anywhere, rearrange your structure
            as the story takes shape, and attach each note directly to the characters and
            places it involves.
          </p>
          <ul className="features-list">
            <li>Infinite canvas: place notes anywhere, zoom out to see the whole arc</li>
            <li>Link notes to characters, locations, events, or any world entity</li>
            <li>Jump from a note directly to the linked entity in your World Builder</li>
            <li>One board per book, so your outline always lives next to your manuscript</li>
          </ul>
          <p className="features-callout">
            Write first, structure later. Or structure first and write with confidence. The Storyboard works either way.
          </p>
        </div>
      </div>
    </section>

    {/* Feature: AI Assistant

        Absent from this page entirely until now, while /compare listed it as one of the
        three things no competitor offers, the landing page tour showed a screenshot of it, and
        an Instagram ad was running for it. This is the page people evaluate on — 35s at
        64% scroll — so leaving it out meant advertising a differentiator the evaluation
        page never mentioned. Positioning follows /compare: it leads with the work around
        the manuscript, not with writing the book (#790). It can draft prose, so the copy
        never denies it; see #798 below. */}
    <section className="features-split">
      <div className="container features-split-inner">
        <div className="features-split-text">
          <div className="features-tag">Smart Help</div>
          <h2>An assistant that can see your book</h2>
          {/* #1025. The owner's words, approved verbatim. True to the product: Chaptery
              ships no model, and the assistant runs only on the key or local model the
              author connects. "Automate tasks in Chaptery" still covers drafting prose,
              which goes through Chaptery's own tools, so it denies nothing (#798). */}
          <p>
            <strong>Chaptery has no AI built in.</strong> The assistant does nothing until
            you connect it to an AI service you choose. Once it's connected, it's designed
            only to automate tasks in Chaptery.
          </p>
          <p>
            It has your chapters and your world in front of it, so asking it to pull the
            characters out of the first four chapters and build them in your World Builder
            is a sentence, not an afternoon. It searches the whole book for you, too: ask how
            many times you wrote "said", and what you reached for instead, and it reads
            every chapter and tallies the answer.
          </p>
          <ul className="features-list">
            <li>Works with the book you have open, not a blank chat box</li>
            <li>Creates characters, locations and events directly in your World Builder</li>
            <li>Links what it creates to the right book, and to each other</li>
            <li>Searches across every chapter for you: how often you used a word, and what you used instead</li>
            <li>Only looks at your book when you ask it something</li>
            <li>Bring your own API key, or run a local model: your provider, your cost, your data</li>
          </ul>
          <p className="features-callout">
            The tedious half of world building, done in the time it takes to ask.
          </p>
          <p>
            <Link to="/features/assistant">See everything you can ask it to do →</Link>
          </p>
        </div>
        {/* An earlier version of this paragraph said the assistant would not write the
            author's story. That was not true: the system prompt casts it as part
            collaborator, part editor and instructs it to write out complete chapter text
            when it makes a change. Drafting prose works, and is meant to.

            It is deliberately not mentioned here. Not mentioning a capability is a
            marketing choice; denying one the product has is a lie a customer discovers
            after paying. The copy leads with where the weight actually sits — of the 53
            tools in the catalogue (19 manuscript, 34 world and storyboard), seven touch
            prose; the rest are structure, world and storyboard, which is also the part
            no competitor offers. (#798)

            "Can see" rather than "has read": writers are the audience most alert to
            models being trained on their work, and "has read your book" describes
            something that happened to their manuscript without them. "Can see" describes
            access they grant, when they ask. Same capability, and the honest one.

            The bullet is scoped to timing on purpose. "Nothing is sent anywhere" would
            have been too broad — a turn does reach the author's chosen provider, and the
            context builder calls our own API for the world. What is verifiable is WHEN:
            the context builder runs per user message and there is no timer or background
            service in the agent, so it looks at nothing until asked.

            This section now has its own screenshot (#901), the world-building request its
            copy leads with. The "said" screenshot stays on the landing and assistant
            pages, which lead with search. It sits five screens down, so the image is
            lazy. */}
        <div className="features-split-visual">
          <picture>
            <source media="(max-width: 700px)" srcSet="/product/assistant-world-mobile.png" width="350" height="424" />
            <img
              src="/product/assistant-world.png"
              width="520"
              height="209"
              alt="The Chaptery Assistant beside the open manuscript, reporting the characters it found in the first four chapters and built in the World Builder, with their roles, traits and relationships"
              className="features-screenshot"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </div>
      </div>
    </section>

    {/* Feature: Writing Dashboard */}
    <section className="features-split">
      <div className="container features-split-inner">
        <div className="features-split-text">
          <div className="features-tag">Build the Habit</div>
          <h2>Watch the words add up</h2>
          <p>
            Finishing a book is a daily habit far more often than a burst of inspiration.
            The Writing Dashboard counts your words as you write, with nothing to start and nothing
            to log, and shows you exactly where you stand today, this week, and this month.
          </p>
          <ul className="features-list">
            <li>Today, this week, and this month at a glance</li>
            <li>Set a daily word goal, and change it whenever you like</li>
            <li>A streak counting every day you hit it</li>
            <li>Words per day for the last 30 days, with your goal line drawn across it</li>
            <li>Tracked per book, and open in your browser as well as the app</li>
          </ul>
          <p className="features-callout">
            Honest numbers, including the days you cut more than you wrote. Revision is writing too.
          </p>
        </div>
        <div className="features-split-visual">
          <img
            src="/img/Writing-dashboard.png"
            alt="Chaptery writing dashboard showing word counts for today, this week and this month, a daily goal, an eleven-day streak, and a words-per-day chart"
            className="features-screenshot"
          />
        </div>
      </div>
    </section>

    {/* Feature: Revisions */}
    <section className="features-split features-split--alt" aria-labelledby="features-revisions-heading">
      <div className="container features-split-inner">
        <div className="features-split-visual">
          <img
            src="/img/features-revision.png"
            width="955"
            height="728"
            alt="Chaptery's Create Revision dialog: Revision 2 is kept as it is now, and you keep writing in Revision 3"
            className="features-screenshot"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="features-split-text">
          <div className="features-tag">Track Revisions</div>
          <h2 id="features-revisions-heading">Every revision of your manuscript, always safe</h2>
          <p>
            A new book starts as Revision 1, your editable working copy. When you are ready
            to lock it in, create a new revision: your writing continues in Revision 2 while
            Revision 1 is frozen exactly as it was. Every revision is preserved in the cloud,
            so your book's history is always safe.
          </p>
          <ul className="features-list">
            <li>A new book begins as Revision 1, your editable working copy</li>
            <li>Create a new revision to lock in the current one and keep writing in the next</li>
            <li>Past revisions are read-only: open them to look back or export them anytime, with no risk of changing them</li>
            <li>Every revision is stored safely in the cloud</li>
          </ul>
          <p className="features-callout">
            You became an author to write, not to wrangle files. Chaptery manages your revisions
            so you can think in books, not folders of documents.
          </p>
        </div>
      </div>
    </section>

    {/* Feature: Access Everywhere */}
    <section id="desktop" className="features-split" aria-labelledby="features-desktop-heading">
      <div className="container features-split-inner">
        <div className="features-split-text">
          <div className="features-tag">Access Everywhere</div>
          <h2 id="features-desktop-heading">A native app for Mac and Windows, plus the web</h2>
          <p>
            Chaptery feels at home on your computer. On the Mac it uses the real menu bar and the
            ⌘ shortcuts you already know; on Windows, a proper menu bar you can reach with Alt or
            F10. One slim toolbar leaves more of the screen for your writing. You write in the
            desktop app, and everything syncs to the cloud, so your Bookshelf, world building,
            storyboard and writing dashboard are ready in any browser too.
          </p>
          <ul className="features-list">
            <li>Real menu bars and familiar shortcuts on Mac and Windows</li>
            <li>One slim toolbar, with Scenes, Notes and the Assistant in one side panel</li>
            <li>Bookshelf, world building, storyboard and dashboard, right in your browser</li>
            <li>Everything synced across your computers and the web</li>
            <li>Secure cloud backup every time you save</li>
          </ul>
        </div>
        <div className="features-split-visual">
          {/* Mac first in the DOM (reading order); its z-index in the CSS puts it in front. */}
          <div className="features-desktop-stack">
            <img
              src="/img/features-mac.png"
              srcSet="/img/features-mac.png 1440w, /img/features-mac@2x.png 2880w"
              sizes="(max-width: 600px) calc(78vw - 31px), 437px"
              alt="Chaptery on macOS, with the Mac menu bar"
              className="features-desktop-shot features-desktop-shot--mac"
              loading="lazy"
              decoding="async"
            />
            <img
              src="/img/features-windows.png"
              srcSet="/img/features-windows.png 1440w, /img/features-windows@2x.png 2880w"
              sizes="(max-width: 600px) calc(78vw - 31px), 437px"
              alt="Chaptery on Windows, with the Windows menu bar"
              className="features-desktop-shot features-desktop-shot--windows"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Comparison teaser */}
    <section className="features-vs">
      <div className="container features-vs-teaser">
        <h2>Built for writers who outgrew the alternatives</h2>
        <p>
          Scrivener, Atticus, Vellum, Google Docs: most tools do just one part of the job.
          See exactly how Chaptery compares, feature by feature.
        </p>
        <Link to="/compare" className="btn-secondary-large">See the Full Comparison →</Link>
      </div>
    </section>

    {/* CTA */}
    <section className="features-cta">
      <div className="container">
        <h2>Ready to bring your world to life?</h2>
        <p>Stop juggling apps. Start finishing books.</p>
        <div className="hero-actions">
          <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('features')}>Get Started</Link>
          <Link to="/pricing" className="btn-secondary-large">View Pricing</Link>
        </div>
      </div>
    </section>

  </div>
  </>
);

export default FeaturesPage;
